import { useTranslation } from "@i18n";
import SelectDropDown, { type SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import {
  clearCostDatabaseRedux,
  discardCostDatabaseDraftVersion,
  getAllVersionsOfCostDatabase,
  getCostDatabase,
  getCostDatabaseById,
  itemListSources,
  publishCostDatabaseVersion,
} from "@Actions/estimation";
import { EstimateCostDatabaseItemsGridNew } from "@Features/estimation";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { useNavigate, useParams } from "react-router-dom";
import { getPortalSettingsTabUrl } from "@Services/utils/helpers";
import { setBreadCrumbData } from "@Reducers/breadcrumbsdata";
import type { RootState } from "src/reducers";
import IconComponent from "@CubeComponents/icons";
import { getCostDatabasesUrl } from "@Features/projects";
import Text from "@CubeComponents/html/Text/Text";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { EstimateCurrency } from "@ServerTypes/estimates/estimates.schema";

type CostDatabaseVersionItem = {
  versionNumber: number | string;
  costDBversionId: string;
};

/**
 * Currency embedded in a cost database. Same shape used in `estimateCurrencySchema`
 * (server SSOT) but the cost-database endpoint returns it as an open `passthrough` object.
 */
type CostDatabaseCurrency = {
  id?: string;
  category?: "Prefix" | "Suffix";
  selectedSymbol?: { name?: string; id?: string };
};

type CostDatabaseRecord = {
  _id?: string;
  name?: string;
  description?: string;
  versionNumber?: number | string;
  currency?: CostDatabaseCurrency;
};

type CostDatabaseProps = {
  costDatabaseId: ObjectIdString;
  showOnlyGrid?: boolean;
  costDatabaseItemsSelection?: (rows: { selectedItems?: CostDatabaseRecord[] } | CostDatabaseRecord[]) => void;
  publishedVersion?: boolean;
};

type CostDatabaseVersionMenuItem = {
  costDBversionId: string;
  text: string;
};

function CostDatabase(props: CostDatabaseProps) {
  const { t } = useTranslation();
  const { costDatabaseId, showOnlyGrid = false, costDatabaseItemsSelection, publishedVersion } = props;
  // Route param is the external entry point for this id; the backend route schema validates it.
  const params = useParams<{ costDatabaseVersionId: ObjectIdString }>();

  const costDatabaseVersionId = params.costDatabaseVersionId;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const costDatabase = useSelector((state: RootState) => state.costDatabase) as CostDatabaseRecord;

  const [allCostDatabaseVersions, setAllCostDatabaseVersions] = useState<CostDatabaseVersionMenuItem[]>([]);
  const [updateChildKey, setUpdateChildKey] = useState(true);
  const [currentOpenCostDatabase, setCurrentOpenCostDatabase] = useState<CostDatabaseRecord>({});

  const { portalSettingInfo } = useSelector((state: RootState) => state);
  const canEditCostDatabase = !!portalSettingInfo?.costDatabase?.editCostDatabases?.enable;
  // `EstimateCurrency` is the grid's declared prop shape, where `id` is `objectIdString`.
  // `currency` is REQUIRED on a CostDatabases document (costDatabase.collection.ts), so the `""`
  // fallbacks are unreachable defence for the first render before `costDatabase` has loaded — the
  // grid below is gated on `currentOpenCostDatabase._id`. Tagged once here, the only place this
  // file mints the empty sentinel; nothing sends it to the backend.
  const safeCurrencyData: EstimateCurrency = {
    id: (currentOpenCostDatabase.currency?.id ?? "") as ObjectIdString,
    category: currentOpenCostDatabase.currency?.category,
    selectedSymbol: { name: currentOpenCostDatabase.currency?.selectedSymbol?.name ?? "" },
  };

  useEffect(() => {
    if (costDatabase._id && !showOnlyGrid) {
      setCurrentOpenCostDatabase({ ...costDatabase });
      dispatch(
        setBreadCrumbData([
          { text: t("Portal Settings") },
          { text: t("Cost Databases"), link: getPortalSettingsTabUrl("costDatabases") },
          { text: costDatabase.name },
        ]),
      );
    }
  }, [costDatabase, showOnlyGrid]);

  const fetchCostDatabase = useCallback(() => {
    dispatch(getCostDatabase({ _id: costDatabaseId, costDatabaseVersionId }));
  }, [costDatabaseId, costDatabaseVersionId]);

  const fetchVersionsOfCostDatabase = useCallback(async () => {
    const response = await getAllVersionsOfCostDatabase({ value: { costDatabaseId } });
    if (response.data.status && Array.isArray(response.data.result)) {
      const result = response.data.result as CostDatabaseVersionItem[];
      const items = result.map(({ versionNumber, costDBversionId }) => ({
        costDBversionId,
        text: String(versionNumber),
      }));

      setAllCostDatabaseVersions(items);
    }
  }, [costDatabaseId]);

  async function publishVersion() {
    const response = await publishCostDatabaseVersion({ costDatabaseId });
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The cost database has been published"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      fetchCostDatabase();
      fetchVersionsOfCostDatabase();
      return;
    }

    dispatch(
      showToastNotification({
        content: t("The cost database could not be published"),
        type: TOAST_TYPE_ERROR,
      }),
    );
  }

  async function discardVersion() {
    const response = await discardCostDatabaseDraftVersion({ costDatabaseId });
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("The cost database draft has been discarded"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      fetchCostDatabase();
      fetchVersionsOfCostDatabase();
      setUpdateChildKey((prev) => !prev);
      return;
    }

    dispatch(
      showToastNotification({
        content: t("The cost database draft could not be discarded"),
        type: TOAST_TYPE_ERROR,
      }),
    );
  }

  function selectActionFromMenu(args: SelectEventArgs) {
    const costDatabaseVersionId = args.item.properties.costDBversionId as string;

    window.open(`${window.location.href}/costDatabaseVersion/${costDatabaseVersionId}`, "_blank");
  }

  const versionsList = useCallback(
    () => (
      <SelectDropDown
        className="me-2.5"
        items={allCostDatabaseVersions}
        select={selectActionFromMenu}
        data-testid="costdatbaseVersionList"
        maxHeight="15.625rem"
      >
        <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON}>
          {currentOpenCostDatabase.versionNumber ? `v${currentOpenCostDatabase.versionNumber}` : "Draft"}
        </ButtonComponent>
      </SelectDropDown>
    ),
    [currentOpenCostDatabase, allCostDatabaseVersions],
  );

  const unmountCostDatabase = useCallback(() => {
    dispatch(clearCostDatabaseRedux());
  }, []);

  const initForCurrentCostDatabase = useCallback(async () => {
    const response = await getCostDatabaseById({ _id: costDatabaseId });
    if (response.data.status && Array.isArray(response.data.result)) {
      setCurrentOpenCostDatabase(response.data.result[0] as CostDatabaseRecord);
    }
  }, [costDatabaseId]);

  useEffect(() => {
    if (!showOnlyGrid) {
      fetchCostDatabase();
      fetchVersionsOfCostDatabase();
      return unmountCostDatabase;
    }
    initForCurrentCostDatabase();
  }, [fetchCostDatabase, fetchVersionsOfCostDatabase, initForCurrentCostDatabase, showOnlyGrid, unmountCostDatabase]);

  const handleBackClick = () => {
    navigate(getCostDatabasesUrl());
  };

  return (
    <div className="flex w-full flex-col">
      {!showOnlyGrid && (
        <div className="flex items-center justify-between py-2 gap-4">
          <div className="flex items-center gap-4 w-full overflow-auto">
            <IconComponent
              className="cursor-pointer"
              name="BackwardArrow"
              onClick={handleBackClick}
              width="1.5rem"
              height="1.5rem"
            />
            <div className="flex w-full flex-col overflow-auto">
              <div className="flex items-center justify-start w-full" data-testid="costDatabaseNameAndVerison">
                <Text className="text-2xl font-semibold" data-testid="costDatabaseName">
                  {currentOpenCostDatabase.name}
                </Text>
                {/* <p
                  className="ms-5 rounded border border-[#192048] px-1 py-0.5 text-xs"
                  data-testid="costDatabaseVersion"
                >
                  {currentOpenCostDatabase.versionNumber ? `v${currentOpenCostDatabase.versionNumber}` : "Draft"}
                </p> */}
              </div>
              <Text className="text-sm" data-testid="costDatabaseDesc">
                {currentOpenCostDatabase.description}
              </Text>
            </div>
          </div>

          <div data-testid="costdatbaseVersionBtn" className="flex gap-4 items-center">
            {costDatabaseVersionId ? (
              <p data-testid="draftOrCostdatbaseVersion">
                {currentOpenCostDatabase.versionNumber ? `v${currentOpenCostDatabase.versionNumber}` : "Draft"}
              </p>
            ) : (
              versionsList()
            )}
            {!costDatabaseVersionId && (
              <ButtonComponent
                data-testid="publishCostdatbase"
                className="me-2.5"
                disabled={!canEditCostDatabase || Boolean(currentOpenCostDatabase.versionNumber)}
                onClick={publishVersion}
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
              >
                {currentOpenCostDatabase.versionNumber ? t("Published") : t("Publish")}
              </ButtonComponent>
            )}
            {!currentOpenCostDatabase.versionNumber && !costDatabaseVersionId && (
              <ButtonComponent
                data-testid="discardCostdatbase"
                className="me-2.5"
                disabled={!canEditCostDatabase}
                onClick={discardVersion}
                buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              >
                {t("Discard")}
              </ButtonComponent>
            )}
          </div>
        </div>
      )}
      {currentOpenCostDatabase._id && (
        <EstimateCostDatabaseItemsGridNew
          costDatabaseId={costDatabaseId}
          costDatabaseVersionId={costDatabaseVersionId}
          showOnlyGrid={showOnlyGrid}
          isStaticData={true}
          selectionEvent={costDatabaseItemsSelection}
          key={String(updateChildKey)}
          source={itemListSources.COST_DATABASE}
          fetchCostDatabase={fetchCostDatabase}
          fetchVersionsOfCostDatabase={fetchVersionsOfCostDatabase}
          currencyData={safeCurrencyData}
          publishedVersion={publishedVersion ?? undefined}
          canEdit={canEditCostDatabase}
        />
      )}
    </div>
  );
}

export default CostDatabase;
